import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Container, Card, Button, Form, InputGroup, Dropdown, Modal, Spinner, Alert, Row, Col, Table, OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import {
  useGetMeetingsQuery,
  useCreateMeetingMutation,
  useDeleteMeetingMutation,
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
  useGetLeadsQuery,
  useGetDealsQuery,
  useGetContactsQuery,
  useGetClientsQuery,
} from '../services/api';
import {
  getDateKeyInTimezone,
  getTimePartsInTimezone,
  getTenantTimezoneFromPreferencesData,
  isSameDayInTimezone,
  toLocaleDateStringInTimezone,
  toLocaleTimeStringInTimezone,
} from '../utils/tenantTimezone';
import {
  preferencesTenantWriteSpread,
  useTenantIdForPreferences,
  useUserPreferencesQueryArg,
} from '../hooks/useTenantIdForPreferences';
import { 
  Search, 
  Plus, 
  ChevronDown, 
  ThreeDotsVertical, 
  X,
  Clock,
  ChevronLeft,
  ChevronRight,
  Phone,
  ChatDots,
  People,
  Flag,
  Envelope,
  CupStraw,
  Calendar3,
  Tag,
  Sticky,
  Person,
  Link45deg,
  Building,
  Gear,
  CurrencyDollar,
  XLg,
  Briefcase,
  InfoCircle,
  ArrowUp,
  ArrowDown,
  GripVertical,
  Pencil,
  Camera,
  CarFront,
  Globe,
  Lock,
  FileEarmark,
  Cart,
  Lightbulb,
  Bell,
  Play,
  Truck,
  Wifi,
  ArrowRepeat,
  Airplane,
  Trophy,
  Key,
  Image,
  Clipboard,
  Speaker,
  GraphUp,
  Scissors,
  Wrench,
  Download,
  Upload,
  Book,
  TrainFront,
  TaxiFront,
  Compass,
  Trash2,
} from 'react-bootstrap-icons';

interface Meeting {
  id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  location?: string;
  meetingType?: 'in-person' | 'video' | 'phone';
  meetingUrl?: string;
  attendees?: string[];
  clientId?: string;
  contactId?: string;
  dealId?: string;
  leadId?: string;
  purposeTypeId?: string;
  notes?: string;
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface MeetingPurposeType {
  id: string;
  name: string;
  color: string;
}

const DEFAULT_MEETING_PURPOSE_TYPES: MeetingPurposeType[] = [
  { id: 'internal-sales-meeting', name: 'Internal Sales Meeting', color: '#ff8c42' },
  { id: 'internal-developer-meetings', name: 'Internal Developer Meetings', color: '#6f42c1' },
  { id: 'client-sales-meetings', name: 'Client Sales Meetings', color: '#0d6efd' },
];

const DEFAULT_PURPOSE_ID = 'client-sales-meetings';
const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

type ActivityType = string;

interface ActivityTypeConfig {
  id: string;
  label: string;
  icon: string;
  active?: boolean;
  order?: number;
}

const ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  phone: Phone,
  'chat-dots': ChatDots,
  people: People,
  clock: Clock,
  flag: Flag,
  envelope: Envelope,
  'cup-straw': CupStraw,
  pencil: Pencil,
  tag: Tag,
  briefcase: Briefcase,
  camera: Camera,
  'car-front': CarFront,
  globe: Globe,
  search: Search,
  lock: Lock,
  'file-earmark': FileEarmark,
  cart: Cart,
  lightbulb: Lightbulb,
  bell: Bell,
  play: Play,
  truck: Truck,
  wifi: Wifi,
  'arrow-repeat': ArrowRepeat,
  airplane: Airplane,
  trophy: Trophy,
  key: Key,
  image: Image,
  clipboard: Clipboard,
  speaker: Speaker,
  'graph-up': GraphUp,
  scissors: Scissors,
  wrench: Wrench,
  download: Download,
  upload: Upload,
  book: Book,
  train: TrainFront,
  taxi: TaxiFront,
  compass: Compass,
};

const DEFAULT_ACTIVITY_TYPES: ActivityTypeConfig[] = [
  { id: 'call', label: 'Call', icon: 'phone', active: true, order: 0 },
  { id: 'text', label: 'Text', icon: 'chat-dots', active: true, order: 1 },
  { id: 'meeting', label: 'Meeting', icon: 'people', active: true, order: 2 },
  { id: 'task', label: 'Task', icon: 'clock', active: true, order: 3 },
  { id: 'deadline', label: 'Deadline', icon: 'flag', active: true, order: 4 },
  { id: 'email', label: 'Email', icon: 'envelope', active: true, order: 5 },
  { id: 'lunch', label: 'Lunch', icon: 'cup-straw', active: true, order: 6 },
];

const normalizeActivityTypeId = (value: string): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const sanitizeActivityTypes = (raw: unknown): ActivityTypeConfig[] => {
  const result: ActivityTypeConfig[] = [];
  const seen = new Set<string>();
  if (!Array.isArray(raw)) return DEFAULT_ACTIVITY_TYPES;
  raw.forEach((item, idx) => {
    if (!item || typeof item !== 'object') return;
    const r = item as Partial<ActivityTypeConfig> & { name?: string };
    const label = String(r.label || r.name || '').trim().replace(/\s+/g, ' ');
    if (!label) return;
    const id = normalizeActivityTypeId(String(r.id || label));
    if (!id || seen.has(id)) return;
    seen.add(id);
    const icon = String(r.icon || 'clock').trim().toLowerCase().replace(/\s+/g, '-');
    result.push({
      id,
      label,
      icon: ICON_MAP[icon] ? icon : 'clock',
      active: r.active !== false,
      order: typeof r.order === 'number' ? r.order : idx,
    });
  });
  result.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  if (result.length === 0) return DEFAULT_ACTIVITY_TYPES;
  return result;
};

const normalizePurposeId = (value: string): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const sanitizeHexColor = (value: unknown, fallback: string): string => {
  const normalized = String(value || '').trim();
  return HEX_COLOR_REGEX.test(normalized) ? normalized.toLowerCase() : fallback;
};

const sanitizeMeetingPurposeTypes = (raw: unknown): MeetingPurposeType[] => {
  const persisted: MeetingPurposeType[] = [];
  const seen = new Set<string>();

  if (Array.isArray(raw)) {
    raw.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const record = item as Partial<MeetingPurposeType> & { label?: string };
      const name = String(record.name || record.label || '').trim().replace(/\s+/g, ' ');
      if (!name) return;

      const id = normalizePurposeId(String(record.id || name));
      if (!id || seen.has(id)) return;
      seen.add(id);

      persisted.push({
        id,
        name,
        color: sanitizeHexColor(record.color, '#0d6efd'),
      });
    });
  }

  const persistedById = new Map(persisted.map((type) => [type.id, type]));
  const defaultIds = new Set(DEFAULT_MEETING_PURPOSE_TYPES.map((type) => type.id));

  const mergedDefaults = DEFAULT_MEETING_PURPOSE_TYPES.map((defaultType) => {
    const persistedOverride = persistedById.get(defaultType.id);
    return persistedOverride
      ? {
          id: defaultType.id,
          name: persistedOverride.name || defaultType.name,
          color: sanitizeHexColor(persistedOverride.color, defaultType.color),
        }
      : defaultType;
  });

  const customTypes = persisted.filter((type) => !defaultIds.has(type.id));
  return [...mergedDefaults, ...customTypes];
};

const Meetings: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');
  const { data: meetingsData, isLoading: isLoadingMeetings, error: meetingsError, refetch: refetchMeetings } = useGetMeetingsQuery({});
  const tenantIdForPrefs = useTenantIdForPreferences();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );
  const [createMeeting, { isLoading: isCreatingMeeting }] = useCreateMeetingMutation();
  const [deleteMeeting, { isLoading: isDeletingMeeting }] = useDeleteMeetingMutation();
  const [updateUserPreferences, { isLoading: isSavingPurposeTypes }] = useUpdateUserPreferencesMutation();
  
  const meetings = useMemo(() => meetingsData || [], [meetingsData]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPurposeFilter, setSelectedPurposeFilter] = useState<'all' | string>('all');
  const [showAddMeetingModal, setShowAddMeetingModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingPurposeTypes, setMeetingPurposeTypes] = useState<MeetingPurposeType[]>(DEFAULT_MEETING_PURPOSE_TYPES);
  const [showManagePurposesModal, setShowManagePurposesModal] = useState(false);
  const [showActivityTypesModal, setShowActivityTypesModal] = useState(false);
  const [showAddActivityTypeModal, setShowAddActivityTypeModal] = useState(false);
  const [newActivityTypeName, setNewActivityTypeName] = useState('');
  const [newActivityTypeIcon, setNewActivityTypeIcon] = useState('phone');
  const [activityTypes, setActivityTypes] = useState<ActivityTypeConfig[]>(DEFAULT_ACTIVITY_TYPES);
  const [newPurposeName, setNewPurposeName] = useState('');
  const [newPurposeColor, setNewPurposeColor] = useState('#14b8a6');
  const [purposeColorDrafts, setPurposeColorDrafts] = useState<Record<string, string>>({});

  const [selectedActivityType, setSelectedActivityType] = useState<ActivityType>('call');
  const [formData, setFormData] = useState({
    title: 'Call',
    description: '',
    startTime: '',
    endTime: '',
    location: '',
    meetingType: 'in-person' as 'in-person' | 'video' | 'phone',
    meetingUrl: '',
    attendees: '',
    clientId: '',
    contactId: '',
    dealId: '',
    leadId: '',
    purposeTypeId: DEFAULT_PURPOSE_ID,
    notes: '',
    tags: '',
    priority: 'normal' as 'low' | 'normal' | 'high',
    freeBusy: 'free' as 'free' | 'busy',
    markAsDone: false,
    videoCallProvider: '' as '' | 'zoom' | 'teams' | 'google-meet',
  });

  const [dealLeadSearch, setDealLeadSearch] = useState('');
  const [showDealLeadDropdown, setShowDealLeadDropdown] = useState(false);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [showPeopleDropdown, setShowPeopleDropdown] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);

  const { data: leadsData } = useGetLeadsQuery({}, { skip: !showAddMeetingModal });
  const { data: dealsData } = useGetDealsQuery({}, { skip: !showAddMeetingModal });
  const { data: contactsData } = useGetContactsQuery({}, { skip: !showAddMeetingModal });
  const { data: clientsData } = useGetClientsQuery({}, { skip: !showAddMeetingModal });

  useEffect(() => {
    const persistedTypes = (userPreferencesData as any)?.preferences?.crmMeetingPurposeTypes;
    setMeetingPurposeTypes(sanitizeMeetingPurposeTypes(persistedTypes));
  }, [userPreferencesData]);

  useEffect(() => {
    const persisted = (userPreferencesData as any)?.preferences?.crmActivityTypes;
    setActivityTypes(sanitizeActivityTypes(persisted));
  }, [userPreferencesData]);

  useEffect(() => {
    const nextDrafts: Record<string, string> = {};
    meetingPurposeTypes.forEach((type) => {
      nextDrafts[type.id] = sanitizeHexColor(type.color, '#0d6efd');
    });
    setPurposeColorDrafts(nextDrafts);
  }, [meetingPurposeTypes]);

  const purposeTypeMap = useMemo(() => {
    const map = new Map<string, MeetingPurposeType>();
    meetingPurposeTypes.forEach((type) => {
      map.set(type.id, type);
    });
    return map;
  }, [meetingPurposeTypes]);

  const leads = useMemo(() => leadsData || [], [leadsData]);
  const deals = useMemo(() => dealsData || [], [dealsData]);
  const contacts = useMemo(() => contactsData || [], [contactsData]);
  const clients = useMemo(() => clientsData || [], [clientsData]);

  const filteredDealsAndLeads = useMemo(() => {
    const q = dealLeadSearch.toLowerCase().trim();
    const dealItems = (deals as { id: string; name?: string }[]).map((d) => ({
      id: d.id,
      type: 'deal' as const,
      label: d.name || 'Untitled deal',
    }));
    const leadItems = (leads as { id: string; name?: string; organization?: string }[]).map((l) => ({
      id: l.id,
      type: 'lead' as const,
      label: l.organization ? `${l.name || 'Untitled'} (${l.organization})` : (l.name || 'Untitled lead'),
    }));
    const all = [...dealItems, ...leadItems];
    if (!q) return all.slice(0, 12);
    return all.filter((item) => item.label.toLowerCase().includes(q)).slice(0, 12);
  }, [deals, leads, dealLeadSearch]);

  const filteredContacts = useMemo(() => {
    const q = peopleSearch.toLowerCase().trim();
    const contactsList = contacts as { id: string; firstName?: string; lastName?: string; organization?: string; email?: string }[];
    const list = contactsList.map((c) => ({
      id: c.id,
      label: c.organization ? `${c.firstName || ''} ${c.lastName || ''}`.trim() + ` (${c.organization})` : `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Untitled contact',
      searchText: `${c.firstName || ''} ${c.lastName || ''} ${c.organization || ''} ${c.email || ''}`.toLowerCase(),
    }));
    if (!q) return list.slice(0, 12).map(({ id, label }) => ({ id, label }));
    return list.filter((item) => item.searchText.includes(q)).slice(0, 12).map(({ id, label }) => ({ id, label }));
  }, [contacts, peopleSearch]);

  const dealLeadDisplayLabel = useMemo(() => {
    if (formData.dealId) {
      const d = (deals as { id: string; name?: string }[]).find((x) => x.id === formData.dealId);
      return d?.name || 'Deal';
    }
    if (formData.leadId) {
      const l = (leads as { id: string; name?: string; organization?: string }[]).find((x) => x.id === formData.leadId);
      return l ? (l.organization ? `${l.name || ''} (${l.organization})` : (l.name || 'Lead')) : 'Lead';
    }
    return '';
  }, [formData.dealId, formData.leadId, deals, leads]);

  const contactDisplayLabel = useMemo(() => {
    if (!formData.contactId) return '';
    const c = (contacts as { id: string; firstName?: string; lastName?: string; organization?: string }[]).find((x) => x.id === formData.contactId);
    if (!c) return 'Contact';
    const name = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return c.organization ? `${name} (${c.organization})` : name;
  }, [formData.contactId, contacts]);

  const orgDisplayLabel = useMemo(() => {
    if (!formData.clientId) return '';
    const c = (clients as { id: string; name?: string; organization?: string }[]).find((x) => x.id === formData.clientId);
    return c?.name || c?.organization || 'Organization';
  }, [formData.clientId, clients]);

  const filteredOrganizations = useMemo(() => {
    const q = orgSearch.toLowerCase().trim();
    const list = (clients as { id: string; name?: string; organization?: string }[])
      .map((c) => ({
        id: c.id,
        label: c.name || c.organization || 'Untitled organization',
      }));
    if (!q) return list.slice(0, 12);
    return list.filter((item) => item.label.toLowerCase().includes(q)).slice(0, 12);
  }, [clients, orgSearch]);

  const getMeetingPurposeId = useCallback((meeting: Meeting): string => {
    const explicitId = normalizePurposeId(String(meeting.purposeTypeId || ''));
    if (explicitId && purposeTypeMap.has(explicitId)) {
      return explicitId;
    }

    const purposeTag = Array.isArray(meeting.tags)
      ? meeting.tags.find((tag) => typeof tag === 'string' && tag.toLowerCase().startsWith('purpose:'))
      : undefined;
    const tagPurposeId = normalizePurposeId(String(purposeTag || '').split(':').slice(1).join(':'));
    if (tagPurposeId && purposeTypeMap.has(tagPurposeId)) {
      return tagPurposeId;
    }

    return DEFAULT_PURPOSE_ID;
  }, [purposeTypeMap]);

  const getMeetingPurposeType = useCallback((meeting: Meeting): MeetingPurposeType => {
    const purposeId = getMeetingPurposeId(meeting);
    return (
      purposeTypeMap.get(purposeId) ||
      DEFAULT_MEETING_PURPOSE_TYPES.find((type) => type.id === DEFAULT_PURPOSE_ID) ||
      DEFAULT_MEETING_PURPOSE_TYPES[0]
    );
  }, [getMeetingPurposeId, purposeTypeMap]);

  const filteredMeetings = useMemo(() => {
    let filtered = [...meetings];

    if (selectedPurposeFilter !== 'all') {
      filtered = filtered.filter((meeting: Meeting) => getMeetingPurposeId(meeting) === selectedPurposeFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((meeting: Meeting) => {
        const title = (meeting.title || '').toLowerCase();
        const description = (meeting.description || '').toLowerCase();
        const location = (meeting.location || '').toLowerCase();
        return title.includes(query) || description.includes(query) || location.includes(query);
      });
    }

    return filtered;
  }, [meetings, searchQuery, selectedPurposeFilter, getMeetingPurposeId]);

  const persistMeetingPurposeTypes = async (nextTypes: MeetingPurposeType[]) => {
    const sanitized = sanitizeMeetingPurposeTypes(nextTypes);
    try {
      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: {
          crmMeetingPurposeTypes: sanitized,
        },
      }).unwrap();
      setMeetingPurposeTypes(sanitized);
    } catch (error: any) {
      console.error('Error saving meeting purpose types:', error);
      alert(`Failed to save meeting purpose types: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const persistActivityTypes = async (nextTypes: ActivityTypeConfig[]) => {
    const sanitized = sanitizeActivityTypes(nextTypes);
    try {
      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: {
          crmActivityTypes: sanitized.map((t, i) => ({ ...t, order: i })),
        },
      }).unwrap();
      setActivityTypes(sanitized);
    } catch (error: any) {
      console.error('Error saving activity types:', error);
      alert(`Failed to save activity types: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleAddActivityType = async () => {
    const customName = newActivityTypeName.trim().replace(/\s+/g, ' ');
    const name = customName || newActivityTypeIcon.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    let id = normalizeActivityTypeId(name);
    if (!id) id = `activity-${Date.now()}`;
    const existing = new Set(activityTypes.map((t) => t.id));
    let suffix = 2;
    while (existing.has(id)) {
      id = `${normalizeActivityTypeId(name)}-${suffix}`;
      suffix += 1;
    }
    const next = [...activityTypes, { id, label: name, icon: newActivityTypeIcon, active: true, order: activityTypes.length }];
    await persistActivityTypes(next);
    setNewActivityTypeName('');
    setNewActivityTypeIcon('phone');
    setShowAddActivityTypeModal(false);
  };

  const handleMoveActivityType = (index: number, direction: 'up' | 'down') => {
    const next = [...activityTypes];
    const j = direction === 'up' ? index - 1 : index + 1;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    const withOrder = next.map((t, i) => ({ ...t, order: i }));
    persistActivityTypes(withOrder);
  };

  const handleToggleActivityTypeActive = (id: string) => {
    const next = activityTypes.map((t) => (t.id === id ? { ...t, active: !t.active } : t));
    persistActivityTypes(next);
  };

  const handleDeleteActivityType = (id: string) => {
    if (!window.confirm('Remove this activity type? It will no longer appear in the list.')) return;
    const next = activityTypes.filter((t) => t.id !== id).map((t, i) => ({ ...t, order: i }));
    persistActivityTypes(next);
  };

  const handleAddMeetingPurposeType = async () => {
    const normalizedName = newPurposeName.trim().replace(/\s+/g, ' ');
    if (!normalizedName) {
      alert('Meeting purpose name is required.');
      return;
    }

    let generatedId = normalizePurposeId(normalizedName);
    if (!generatedId) {
      generatedId = `purpose-${Date.now()}`;
    }

    const existingPurposeIds = new Set(meetingPurposeTypes.map((type) => type.id));
    let suffix = 2;
    while (existingPurposeIds.has(generatedId)) {
      generatedId = `${normalizePurposeId(normalizedName)}-${suffix}`;
      suffix += 1;
    }

    const nextTypes: MeetingPurposeType[] = [
      ...meetingPurposeTypes,
      {
        id: generatedId,
        name: normalizedName,
        color: sanitizeHexColor(newPurposeColor, '#0d6efd'),
      },
    ];

    await persistMeetingPurposeTypes(nextTypes);
    setNewPurposeName('');
    setNewPurposeColor('#14b8a6');
  };

  const handlePurposeColorDraftChange = (purposeId: string, color: string) => {
    setPurposeColorDrafts((prev) => ({
      ...prev,
      [purposeId]: sanitizeHexColor(color, prev[purposeId] || '#0d6efd'),
    }));
  };

  const handleSavePurposeColor = async (purposeId: string) => {
    const requestedColor = sanitizeHexColor(
      purposeColorDrafts[purposeId],
      meetingPurposeTypes.find((type) => type.id === purposeId)?.color || '#0d6efd'
    );

    const nextTypes = meetingPurposeTypes.map((type) =>
      type.id === purposeId ? { ...type, color: requestedColor } : type
    );

    await persistMeetingPurposeTypes(nextTypes);
  };

  const meetingsByDate = useMemo(() => {
    const grouped: Record<string, Meeting[]> = {};
    
    filteredMeetings.forEach((meeting: Meeting) => {
      const dateKey = getDateKeyInTimezone(meeting.startTime, tenantTimezone);
      if (!dateKey) return;
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(meeting);
    });

    return grouped;
  }, [filteredMeetings, tenantTimezone]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1, 12, 0, 0, 0);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day, 12, 0, 0, 0));
    }

    return days;
  };

  const getStartOfWeek = (date: Date) => {
    const start = new Date(date);
    start.setHours(12, 0, 0, 0);
    start.setDate(start.getDate() - start.getDay());
    return start;
  };

  const getWeekDays = (date: Date) => {
    const start = getStartOfWeek(date);
    return Array.from({ length: 7 }, (_, idx) => {
      const day = new Date(start);
      day.setDate(start.getDate() + idx);
      return day;
    });
  };

  const handleSaveMeeting = async () => {
    try {
      const rawTagList = formData.tags
        ? formData.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
        : [];
      const nonPurposeTags = rawTagList.filter((tag) => !tag.toLowerCase().startsWith('purpose:'));
      const purposeTag = `purpose:${formData.purposeTypeId || DEFAULT_PURPOSE_ID}`;
      const tags = Array.from(new Set([...nonPurposeTags, purposeTag]));

      const meetingData = {
        title: formData.title,
        description: formData.description || undefined,
        startTime: formData.startTime,
        endTime: formData.endTime,
        location: formData.location || undefined,
        meetingType: formData.meetingType,
        meetingUrl: formData.meetingUrl || undefined,
        attendees: formData.attendees ? formData.attendees.split(',').map(a => a.trim()) : [],
        clientId: formData.clientId || undefined,
        contactId: formData.contactId || undefined,
        dealId: formData.dealId || undefined,
        leadId: formData.leadId || undefined,
        purposeTypeId: formData.purposeTypeId || DEFAULT_PURPOSE_ID,
        notes: formData.notes || undefined,
        tags,
        visibility: 'personal'
      };

      await createMeeting(meetingData).unwrap();
      
      setFormData({
        title: 'Call',
        description: '',
        startTime: '',
        endTime: '',
        location: '',
        meetingType: 'in-person',
        meetingUrl: '',
        attendees: '',
        clientId: '',
        contactId: '',
        dealId: '',
        leadId: '',
        purposeTypeId: DEFAULT_PURPOSE_ID,
        notes: '',
        tags: '',
        priority: 'normal',
        freeBusy: 'free',
        markAsDone: false,
        videoCallProvider: '',
      });

      setShowAddMeetingModal(false);
      refetchMeetings();
    } catch (error: any) {
      console.error('Error creating meeting:', error);
      alert(`Failed to create meeting: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleDeleteSelectedMeeting = async () => {
    if (!selectedMeeting?.id) return;

    const confirmed = window.confirm(`Delete meeting "${selectedMeeting.title}"?`);
    if (!confirmed) return;

    try {
      await deleteMeeting(selectedMeeting.id).unwrap();
      setShowMeetingModal(false);
      setSelectedMeeting(null);
      refetchMeetings();
    } catch (error: any) {
      console.error('Error deleting meeting:', error);
      alert(`Failed to delete meeting: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getDefaultStartEnd = () => {
    const now = new Date();
    const end = new Date(now.getTime() + 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const toLocal = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return { start: toLocal(now), end: toLocal(end) };
  };

  const handleOpenAddMeetingModal = () => {
    const { start, end } = getDefaultStartEnd();
    setSelectedActivityType('call');
    setFormData({
      title: 'Call',
      description: '',
      startTime: start,
      endTime: end,
      location: '',
      meetingType: 'in-person',
      meetingUrl: '',
      attendees: '',
      clientId: '',
      contactId: '',
      dealId: '',
      leadId: '',
      purposeTypeId: DEFAULT_PURPOSE_ID,
      notes: '',
      tags: '',
      priority: 'normal',
      freeBusy: 'free',
      markAsDone: false,
      videoCallProvider: '',
    });
    setDealLeadSearch('');
    setPeopleSearch('');
    setOrgSearch('');
    setShowDealLeadDropdown(false);
    setShowPeopleDropdown(false);
    setShowOrgDropdown(false);
    setShowAddMeetingModal(true);
  };

  const handleSelectActivityType = (type: ActivityType) => {
    const config = activityTypes.find((t) => t.id === type);
    if (config) {
      setSelectedActivityType(type);
      setFormData((prev) => ({ ...prev, title: config.label }));
    }
  };

  const getModalDisplayDate = () => {
    if (formData.startTime) {
      return new Date(formData.startTime);
    }
    return new Date();
  };

  const shiftModalDay = (delta: number) => {
    const d = getModalDisplayDate();
    d.setDate(d.getDate() + delta);
    const pad = (n: number) => String(n).padStart(2, '0');
    const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T`;
    const startTime = formData.startTime || getDefaultStartEnd().start;
    const endTime = formData.endTime || getDefaultStartEnd().end;
    const startH = startTime.slice(11, 16);
    const endH = endTime.slice(11, 16);
    setFormData(prev => ({
      ...prev,
      startTime: dateStr + startH,
      endTime: dateStr + endH,
    }));
  };

  const handleModalPrevDay = () => shiftModalDay(-1);
  const handleModalNextDay = () => shiftModalDay(1);

  const handleModalToday = () => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T`;
    const startTime = formData.startTime || getDefaultStartEnd().start;
    const endTime = formData.endTime || getDefaultStartEnd().end;
    const startH = startTime.slice(11, 16);
    const endH = endTime.slice(11, 16);
    setFormData(prev => ({
      ...prev,
      startTime: dateStr + startH,
      endTime: dateStr + endH,
    }));
  };

  const handlePreviousPeriod = () => {
    if (viewMode === 'week') {
      const previousWeek = new Date(currentDate);
      previousWeek.setDate(previousWeek.getDate() - 7);
      setCurrentDate(previousWeek);
      return;
    }

    if (viewMode === 'day') {
      const previousDay = new Date(currentDate);
      previousDay.setDate(previousDay.getDate() - 1);
      setCurrentDate(previousDay);
      return;
    }

    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextPeriod = () => {
    if (viewMode === 'week') {
      const nextWeek = new Date(currentDate);
      nextWeek.setDate(nextWeek.getDate() + 7);
      setCurrentDate(nextWeek);
      return;
    }

    if (viewMode === 'day') {
      const nextDay = new Date(currentDate);
      nextDay.setDate(nextDay.getDate() + 1);
      setCurrentDate(nextDay);
      return;
    }

    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return toLocaleTimeStringInTimezone(date, 'en-US', { hour: 'numeric', minute: '2-digit' }, tenantTimezone);
  };

  const isToday = (date: Date | null) => {
    if (!date) return false;
    return isSameDayInTimezone(date, new Date(), tenantTimezone);
  };

  const getMeetingsForDate = (date: Date | null) => {
    if (!date) return [];
    const dateKey = getDateKeyInTimezone(date, tenantTimezone);
    if (!dateKey) return [];
    return meetingsByDate[dateKey] || [];
  };

  const getSortedMeetingsForDate = (date: Date | null) =>
    [...getMeetingsForDate(date)].sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );

  if (isLoadingMeetings) {
    return (
      <Container fluid className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  if (meetingsError) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Error loading meetings</Alert.Heading>
          <p>{(meetingsError as any)?.data?.message || 'Failed to load meetings'}</p>
          <Button onClick={() => refetchMeetings()}>Retry</Button>
        </Alert>
      </Container>
    );
  }

  const days = getDaysInMonth(currentDate);
  const weekDays = getWeekDays(currentDate);
  const selectedDayMeetings = getSortedMeetingsForDate(currentDate);
  const periodLabel = (() => {
    if (viewMode === 'month') {
      return toLocaleDateStringInTimezone(currentDate, 'en-US', { month: 'long', year: 'numeric' }, tenantTimezone);
    }
    if (viewMode === 'week') {
      const start = weekDays[0];
      const end = weekDays[6];
      const startLabel = toLocaleDateStringInTimezone(start, 'en-US', { month: 'short', day: 'numeric' }, tenantTimezone);
      const endLabel = toLocaleDateStringInTimezone(end, 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }, tenantTimezone);
      return `${startLabel} - ${endLabel}`;
    }
    return toLocaleDateStringInTimezone(currentDate, 'en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }, tenantTimezone);
  })();

  return (
    <Container fluid className="p-0" style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      {/* Page Header - same as Leads/Clients/Contacts */}
      <div style={{ backgroundColor: '#ffffff', paddingTop: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #dee2e6' }}>
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <h5 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: 500, color: '#212529' }}>Meetings</h5>
            </div>
            <div className="d-flex align-items-center gap-2">
              <InputGroup size="sm" style={{ width: '200px' }}>
                <InputGroup.Text style={{ backgroundColor: 'white', borderRight: 'none', fontSize: '0.75rem' }}>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Kross Global"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ borderLeft: 'none', fontSize: '0.75rem' }}
                  size="sm"
                />
              </InputGroup>
            </div>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <div className="py-3" style={{ fontSize: '0.75rem' }}>
      {/* Action Bar */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button 
            variant="primary" 
            size="sm" 
            onClick={handleOpenAddMeetingModal}
            style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}
          >
            <Plus size={14} className="me-1" />
            Meeting
          </Button>
          <span style={{ fontSize: '0.75rem', marginLeft: '0.5rem' }}>
            {filteredMeetings.length} meetings
          </span>
          <Form.Select
            size="sm"
            value={selectedPurposeFilter}
            onChange={(e) => setSelectedPurposeFilter(e.target.value)}
            style={{ width: '240px', fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}
          >
            <option value="all">All meeting purposes</option>
            {meetingPurposeTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Form.Select>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowManagePurposesModal(true)}
            style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}
          >
            Manage purposes
          </Button>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={handleToday}
            style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}
          >
            Today
          </Button>
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={handlePreviousPeriod}
            style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}
          >
            <ChevronLeft size={14} />
          </Button>
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={handleNextPeriod}
            style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}
          >
            <ChevronRight size={14} />
          </Button>
          <Dropdown>
            <Dropdown.Toggle 
              variant="outline-secondary" 
              size="sm"
              style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}
            >
              {viewMode.charAt(0).toUpperCase() + viewMode.slice(1)}
              <ChevronDown size={12} className="ms-1" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setViewMode('month')}>Month</Dropdown.Item>
              <Dropdown.Item onClick={() => setViewMode('week')}>Week</Dropdown.Item>
              <Dropdown.Item onClick={() => setViewMode('day')}>Day</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button variant="link" size="sm" style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}>
            <ThreeDotsVertical size={14} />
          </Button>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'month' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>{periodLabel}</h6>
          </div>
          <Table bordered style={{ fontSize: '0.75rem', width: '100%', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <th
                    key={day}
                    style={{
                      width: '14.2857%',
                      padding: '0.5rem',
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa'
                    }}
                  >
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: Math.ceil(days.length / 7) }).map((_, weekIndex) => (
                <tr key={weekIndex}>
                  {days.slice(weekIndex * 7, (weekIndex + 1) * 7).map((date, dayIndex) => {
                    const dayMeetings = getMeetingsForDate(date);
                    return (
                      <td
                        key={dayIndex}
                        style={{
                          width: '14.2857%',
                          padding: '0.5rem',
                          verticalAlign: 'top',
                          height: '120px',
                          backgroundColor: isToday(date) ? '#e7f3ff' : 'white',
                          border: isToday(date) ? '2px solid #0d6efd' : '1px solid #dee2e6'
                        }}
                      >
                        {date && (
                          <>
                            <div
                              style={{
                                fontWeight: isToday(date) ? 'bold' : 'normal',
                                marginBottom: '0.25rem',
                                color: isToday(date) ? '#0d6efd' : 'inherit'
                              }}
                            >
                              {toLocaleDateStringInTimezone(date, 'en-US', { day: 'numeric' }, tenantTimezone)}
                            </div>
                            <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                              {dayMeetings.slice(0, 3).map((meeting: Meeting) => {
                                const purpose = getMeetingPurposeType(meeting);
                                return (
                                <div
                                  key={meeting.id}
                                  className="mb-1"
                                  onClick={() => {
                                    setSelectedMeeting(meeting);
                                    setShowMeetingModal(true);
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    fontSize: '0.65rem',
                                    padding: '0.15rem',
                                    backgroundColor: purpose.color,
                                    color: 'white',
                                    borderRadius: '2px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={`${meeting.title} (${purpose.name})`}
                                >
                                  {formatTime(meeting.startTime)} {meeting.title}
                                </div>
                                );
                              })}
                              {dayMeetings.length > 3 && (
                                <div style={{ fontSize: '0.65rem', color: '#6c757d' }}>
                                  +{dayMeetings.length - 3} more
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {viewMode === 'week' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>{periodLabel}</h6>
          </div>
          <Table bordered style={{ fontSize: '0.75rem', width: '100%', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                {weekDays.map((day) => (
                  <th
                    key={day.toISOString()}
                    style={{
                      width: '14.2857%',
                      padding: '0.5rem',
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa',
                    }}
                  >
                    <div>{toLocaleDateStringInTimezone(day, 'en-US', { weekday: 'short' }, tenantTimezone)}</div>
                    <div style={{ fontSize: '0.68rem', color: '#6c757d' }}>
                      {toLocaleDateStringInTimezone(day, 'en-US', { month: 'short', day: 'numeric' }, tenantTimezone)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {weekDays.map((day) => {
                  const dayMeetings = getSortedMeetingsForDate(day);
                  return (
                    <td
                      key={`week-cell-${day.toISOString()}`}
                      style={{
                        width: '14.2857%',
                        padding: '0.5rem',
                        verticalAlign: 'top',
                        height: '380px',
                        backgroundColor: isToday(day) ? '#e7f3ff' : 'white',
                        border: isToday(day) ? '2px solid #0d6efd' : '1px solid #dee2e6',
                      }}
                    >
                      <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
                        {dayMeetings.length === 0 && (
                          <div style={{ fontSize: '0.68rem', color: '#adb5bd' }}>No meetings</div>
                        )}
                        {dayMeetings.map((meeting) => {
                          const purpose = getMeetingPurposeType(meeting);
                          return (
                            <div
                              key={meeting.id}
                              className="mb-1"
                              onClick={() => {
                                setSelectedMeeting(meeting);
                                setShowMeetingModal(true);
                              }}
                              style={{
                                cursor: 'pointer',
                                fontSize: '0.65rem',
                                padding: '0.2rem',
                                backgroundColor: purpose.color,
                                color: 'white',
                                borderRadius: '2px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={`${meeting.title} (${purpose.name})`}
                            >
                              {formatTime(meeting.startTime)} {meeting.title}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </Table>
        </div>
      )}

      {viewMode === 'day' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>{periodLabel}</h6>
          </div>
          <Card>
            <Card.Body style={{ padding: '0.75rem' }}>
              {selectedDayMeetings.length === 0 ? (
                <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>No meetings scheduled for this day.</div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {selectedDayMeetings.map((meeting) => {
                    const purpose = getMeetingPurposeType(meeting);
                    return (
                      <div
                        key={meeting.id}
                        onClick={() => {
                          setSelectedMeeting(meeting);
                          setShowMeetingModal(true);
                        }}
                        style={{
                          cursor: 'pointer',
                          border: '1px solid #dee2e6',
                          borderLeft: `4px solid ${purpose.color}`,
                          borderRadius: '4px',
                          padding: '0.6rem',
                          backgroundColor: '#fff',
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#212529' }}>
                            {meeting.title}
                          </div>
                          <span
                            className="rounded-pill"
                            style={{
                              display: 'inline-block',
                              backgroundColor: purpose.color,
                              color: '#fff',
                              fontWeight: 500,
                              fontSize: '0.66rem',
                              padding: '0.22rem 0.45rem',
                              lineHeight: 1
                            }}
                          >
                            {purpose.name}
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                          <Clock size={12} />
                          <span>{formatTime(meeting.startTime)} - {formatTime(meeting.endTime)}</span>
                        </div>
                        {meeting.location && (
                          <div style={{ fontSize: '0.7rem', color: '#6c757d', marginTop: '0.2rem' }}>
                            {meeting.location}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      )}
      </div>

      {/* Schedule an activity Modal - Pipedrive-style */}
      <Modal show={showAddMeetingModal} onHide={() => setShowAddMeetingModal(false)} size="lg">
        <Modal.Header style={{ fontSize: '1rem', padding: '0.75rem 1rem', borderBottom: '1px solid #dee2e6' }}>
          <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 500 }}>Schedule an activity</Modal.Title>
          <Button variant="link" onClick={() => setShowAddMeetingModal(false)} style={{ padding: 0, fontSize: '18px', color: '#6c757d' }}>
            <X />
          </Button>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.75rem', padding: '1rem', display: 'flex', gap: '1.5rem' }}>
          {/* Left: Activity form */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Activity title + icon row */}
            <div className="mb-3">
              <Form.Control
                size="sm"
                value={formData.title}
                onChange={(e) => handleFormChange('title', e.target.value)}
                style={{ fontSize: '1rem', padding: '0.4rem 0.5rem', marginBottom: '0.5rem' }}
                placeholder="Activity title"
              />
              <div className="d-flex gap-1 flex-wrap">
                {activityTypes.filter((t) => t.active !== false).map(({ id, label, icon }) => {
                  const Icon = ICON_MAP[icon] || Clock;
                  return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleSelectActivityType(id)}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 6,
                      border: '1px solid #dee2e6',
                      backgroundColor: selectedActivityType === id ? '#0d6efd' : '#fff',
                      color: selectedActivityType === id ? '#fff' : '#6c757d',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                    title={label}
                  >
                    <Icon size={18} />
                  </button>
                  );
                })}
              </div>
            </div>

            {/* Date/time row - single date, start/end times */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <Clock size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
              <Form.Control
                type="date"
                size="sm"
                value={formData.startTime ? formData.startTime.slice(0, 10) : ''}
                onChange={(e) => {
                  const date = e.target.value;
                  const startTime = formData.startTime ? formData.startTime.slice(11, 16) : '12:00';
                  const endTime = formData.endTime ? formData.endTime.slice(11, 16) : '13:00';
                  setFormData((p) => ({
                    ...p,
                    startTime: `${date}T${startTime}`,
                    endTime: `${date}T${endTime}`,
                  }));
                }}
                style={{ width: 140, fontSize: '0.75rem' }}
              />
              <Form.Control
                type="time"
                size="sm"
                value={formData.startTime ? formData.startTime.slice(11, 16) : ''}
                onChange={(e) => {
                  const date = formData.startTime
                    ? formData.startTime.slice(0, 10)
                    : (getDateKeyInTimezone(new Date(), tenantTimezone) || '');
                  handleFormChange('startTime', `${date}T${e.target.value}`);
                }}
                style={{ width: 100, fontSize: '0.75rem' }}
              />
              <span style={{ color: '#6c757d' }}>–</span>
              <Form.Control
                type="time"
                size="sm"
                value={formData.endTime ? formData.endTime.slice(11, 16) : ''}
                onChange={(e) => {
                  const date = formData.endTime
                    ? formData.endTime.slice(0, 10)
                    : formData.startTime?.slice(0, 10)
                      || (getDateKeyInTimezone(new Date(), tenantTimezone) || '');
                  handleFormChange('endTime', `${date}T${e.target.value}`);
                }}
                style={{ width: 100, fontSize: '0.75rem' }}
              />
            </div>

            {/* Priority */}
            <div className="d-flex align-items-center gap-2 mb-2">
              <Calendar3 size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
              <Form.Select
                size="sm"
                value={formData.priority}
                onChange={(e) => handleFormChange('priority', e.target.value)}
                style={{ width: 120, fontSize: '0.75rem' }}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </Form.Select>
            </div>

            {/* Purpose of this activity */}
            <div className="d-flex align-items-center gap-2 mb-2">
              <Briefcase size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
              <Form.Select
                size="sm"
                value={formData.purposeTypeId}
                onChange={(e) => handleFormChange('purposeTypeId', e.target.value)}
                style={{ flex: 1, maxWidth: 280, fontSize: '0.75rem' }}
              >
                {meetingPurposeTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Location */}
            <div className="d-flex align-items-center gap-2 mb-2">
              <Link45deg size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
              <Form.Control
                size="sm"
                value={formData.location}
                onChange={(e) => handleFormChange('location', e.target.value)}
                placeholder="Add location"
                style={{ flex: 1, fontSize: '0.75rem' }}
              />
            </div>

            {/* Install video call integration */}
            <div className="d-flex align-items-center gap-2 mb-3">
              <Camera size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
              <Form.Select
                size="sm"
                value={formData.videoCallProvider}
                onChange={(e) => {
                  const val = e.target.value as '' | 'zoom' | 'teams' | 'google-meet';
                  setFormData((p) => ({
                    ...p,
                    videoCallProvider: val,
                    meetingType: val ? 'video' : 'in-person',
                    meetingUrl: val ? p.meetingUrl : '',
                  }));
                }}
                style={{ flex: 1, maxWidth: 260, fontSize: '0.75rem' }}
              >
                <option value="">Install video call integration</option>
                <option value="zoom">Zoom meeting</option>
                <option value="teams">Microsoft Teams meeting</option>
                <option value="google-meet">Google Meet meeting</option>
              </Form.Select>
            </div>

            {formData.videoCallProvider && (
              <div className="d-flex align-items-center gap-2 mb-3" style={{ marginLeft: 24 }}>
                <Form.Control
                  size="sm"
                  value={formData.meetingUrl}
                  onChange={(e) => handleFormChange('meetingUrl', e.target.value)}
                  placeholder={
                    formData.videoCallProvider === 'google-meet'
                      ? 'Paste Google Meet link here'
                      : formData.videoCallProvider === 'zoom'
                        ? 'Paste Zoom link here'
                        : 'Paste Teams link here'
                  }
                  style={{ flex: 1, fontSize: '0.75rem' }}
                />
              </div>
            )}

            {/* Free/Busy */}
            <div className="d-flex align-items-center gap-2 mb-3">
              <Tag size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
              <Form.Select
                size="sm"
                value={formData.freeBusy}
                onChange={(e) => handleFormChange('freeBusy', e.target.value)}
                style={{ width: 100, fontSize: '0.75rem' }}
              >
                <option value="free">Free</option>
                <option value="busy">Busy</option>
              </Form.Select>
              <OverlayTrigger
                trigger="click"
                placement="top"
                rootClose
                overlay={
                  <Popover id="free-busy-popover" style={{ maxWidth: 320 }}>
                    <Popover.Header as="h3" className="fw-bold" style={{ fontSize: '0.9rem' }}>
                      Free/Busy
                    </Popover.Header>
                    <Popover.Body style={{ fontSize: '0.8rem' }}>
                      This option controls whether you are shown as available or unavailable for other meetings at the same time, both in your external calendar and Scheduler.
                    </Popover.Body>
                  </Popover>
                }
              >
                <span style={{ color: '#6c757d', cursor: 'pointer' }} role="button" tabIndex={0}>
                  <InfoCircle size={16} />
                </span>
              </OverlayTrigger>
            </div>

            {/* Notes */}
            <div className="mb-3">
              <div className="d-flex align-items-center gap-2 mb-1">
                <Sticky size={16} style={{ color: '#6c757d' }} />
                <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Notes</span>
              </div>
              <Form.Control
                as="textarea"
                rows={4}
                size="sm"
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                placeholder="Notes are visible within the app, but not to event guests."
                style={{ fontSize: '0.75rem', padding: '0.4rem', backgroundColor: '#fffef0', border: '1px solid #dee2e6' }}
              />
              <div style={{ fontSize: '0.65rem', color: '#6c757d', marginTop: '0.25rem' }}>
                Notes are visible within the app, but not to event guests.
              </div>
            </div>

            {/* Related entities */}
            <div className="mb-2" style={{ width: '100%' }}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <Person size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                <Form.Control size="sm" value="You" readOnly style={{ flex: 1, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }} />
              </div>
              <div className="d-flex align-items-center gap-2 mb-1" style={{ position: 'relative' }}>
                <CurrencyDollar size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                <div style={{ flex: 1, position: 'relative' }}>
                  <Form.Control
                    size="sm"
                    value={dealLeadDisplayLabel || dealLeadSearch}
                    onChange={(e) => {
                      setDealLeadSearch(e.target.value);
                      setShowDealLeadDropdown(true);
                      if (formData.dealId || formData.leadId) {
                        setFormData((p) => ({ ...p, dealId: '', leadId: '' }));
                      }
                    }}
                    onFocus={() => setShowDealLeadDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDealLeadDropdown(false), 200)}
                    placeholder="Deal or Lead"
                    style={{ width: '100%', fontSize: '0.75rem' }}
                  />
                  {(formData.dealId || formData.leadId) && (
                    <button
                      type="button"
                      onClick={() => setFormData((p) => ({ ...p, dealId: '', leadId: '' }))}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}
                    >
                      <XLg size={12} />
                    </button>
                  )}
                  {showDealLeadDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                      {filteredDealsAndLeads.length === 0 ? (
                        <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No deals or leads found</div>
                      ) : (
                        filteredDealsAndLeads.map((item) => (
                          <div
                            key={`${item.type}-${item.id}`}
                            onClick={() => {
                              if (item.type === 'deal') {
                                setFormData((p) => ({ ...p, dealId: item.id, leadId: '' }));
                              } else {
                                setFormData((p) => ({ ...p, leadId: item.id, dealId: '' }));
                              }
                              setDealLeadSearch('');
                              setShowDealLeadDropdown(false);
                            }}
                            style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {item.label}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 mb-1" style={{ position: 'relative' }}>
                <People size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                <div style={{ flex: 1, position: 'relative' }}>
                  <Form.Control
                    size="sm"
                    value={contactDisplayLabel || peopleSearch}
                    onChange={(e) => {
                      setPeopleSearch(e.target.value);
                      setShowPeopleDropdown(true);
                      if (formData.contactId) setFormData((p) => ({ ...p, contactId: '' }));
                    }}
                    onFocus={() => setShowPeopleDropdown(true)}
                    onBlur={() => setTimeout(() => setShowPeopleDropdown(false), 200)}
                    placeholder="People"
                    style={{ width: '100%', fontSize: '0.75rem' }}
                  />
                  {formData.contactId && (
                    <button
                      type="button"
                      onClick={() => setFormData((p) => ({ ...p, contactId: '' }))}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}
                    >
                      <XLg size={12} />
                    </button>
                  )}
                  {showPeopleDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                      {filteredContacts.length === 0 ? (
                        <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No contacts found</div>
                      ) : (
                        filteredContacts.map((item) => (
                          <div
                            key={item.id}
                            onClick={() => {
                              setFormData((p) => ({ ...p, contactId: item.id }));
                              setPeopleSearch('');
                              setShowPeopleDropdown(false);
                            }}
                            style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {item.label}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center gap-2" style={{ position: 'relative' }}>
                <Building size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                <div style={{ flex: 1, position: 'relative' }}>
                  <Form.Control
                    size="sm"
                    value={orgDisplayLabel || orgSearch}
                    onChange={(e) => {
                      setOrgSearch(e.target.value);
                      setShowOrgDropdown(true);
                      if (formData.clientId) setFormData((p) => ({ ...p, clientId: '' }));
                    }}
                    onFocus={() => setShowOrgDropdown(true)}
                    onBlur={() => setTimeout(() => setShowOrgDropdown(false), 200)}
                    placeholder="Organization"
                    style={{ width: '100%', fontSize: '0.75rem' }}
                  />
                  {formData.clientId && (
                    <button
                      type="button"
                      onClick={() => setFormData((p) => ({ ...p, clientId: '' }))}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}
                    >
                      <XLg size={12} />
                    </button>
                  )}
                  {showOrgDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                      {filteredOrganizations.length === 0 ? (
                        <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No organizations found</div>
                      ) : (
                        filteredOrganizations.map((item) => (
                          <div
                            key={item.id}
                            onClick={() => {
                              setFormData((p) => ({ ...p, clientId: item.id }));
                              setOrgSearch('');
                              setShowOrgDropdown(false);
                            }}
                            style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {item.label}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right: Calendar preview with timeline */}
          <div style={{ width: 300, flexShrink: 0, borderLeft: '1px solid #dee2e6', paddingLeft: '1rem' }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                {toLocaleDateStringInTimezone(
                  getModalDisplayDate(),
                  'en-US',
                  { weekday: 'long', month: 'long', day: 'numeric' },
                  tenantTimezone
                )}
              </div>
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  onClick={handleModalPrevDay}
                  style={{ border: 'none', background: 'none', padding: '0.2rem', cursor: 'pointer', color: '#6c757d' }}
                  title="Previous day"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  onClick={handleModalNextDay}
                  style={{ border: 'none', background: 'none', padding: '0.2rem', cursor: 'pointer', color: '#6c757d' }}
                  title="Next day"
                >
                  <ChevronRight size={18} />
                </button>
                <Button variant="outline-secondary" size="sm" onClick={handleModalToday} style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}>
                  Today
                </Button>
              </div>
            </div>
            <div style={{ display: 'flex', overflow: 'hidden', borderRadius: 4, border: '1px solid #dee2e6' }}>
              <div style={{ width: 44, flexShrink: 0, backgroundColor: '#f8f9fa', padding: '0.25rem 0' }}>
                {Array.from({ length: 12 }, (_, i) => i + 11).map((hour) => (
                  <div
                    key={hour}
                    style={{
                      height: 36,
                      fontSize: '0.65rem',
                      color: '#6c757d',
                      paddingLeft: '0.35rem',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                  </div>
                ))}
              </div>
              <div style={{ flex: 1, position: 'relative', minHeight: 432 }}>
                {Array.from({ length: 12 }, (_, i) => i + 11).map((hour) => (
                  <div
                    key={hour}
                    style={{
                      height: 36,
                      borderBottom: '1px solid #eee',
                    }}
                  />
                ))}
                {(() => {
                  const displayDate = getModalDisplayDate();
                  const today = new Date();
                  const isToday = isSameDayInTimezone(displayDate, today, tenantTimezone);
                  const nowTimeParts = getTimePartsInTimezone(today, tenantTimezone);
                  const nowHour = nowTimeParts
                    ? nowTimeParts.hour + nowTimeParts.minute / 60
                    : today.getHours() + today.getMinutes() / 60;
                  const SLOT_HEIGHT = 36;
                  const topPx = isToday && nowHour >= 11 && nowHour < 22
                    ? (nowHour - 11) * SLOT_HEIGHT
                    : -999;
                  return (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: topPx,
                        height: 2,
                        backgroundColor: '#dc3545',
                        display: 'flex',
                        alignItems: 'center',
                        zIndex: 2,
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.6rem',
                          color: '#dc3545',
                          backgroundColor: '#fff',
                          padding: '0 0.2rem',
                          marginLeft: 4,
                        }}
                      >
                        {toLocaleTimeStringInTimezone(
                          today,
                          'en-US',
                          { hour: 'numeric', minute: '2-digit' },
                          tenantTimezone
                        )}
                      </span>
                    </div>
                  );
                })()}
                {formData.startTime && formData.endTime && (() => {
                  const start = new Date(formData.startTime);
                  const end = new Date(formData.endTime);
                  const displayDate = getModalDisplayDate();
                  const sameDay = isSameDayInTimezone(start, displayDate, tenantTimezone);
                  if (!sameDay) return null;
                  const startTimeParts = getTimePartsInTimezone(start, tenantTimezone);
                  const endTimeParts = getTimePartsInTimezone(end, tenantTimezone);
                  if (!startTimeParts || !endTimeParts) return null;
                  let startHour = startTimeParts.hour + startTimeParts.minute / 60;
                  let endHour = endTimeParts.hour + endTimeParts.minute / 60;
                  if (endHour < startHour) endHour += 24;
                  const SLOT_HEIGHT = 36;
                  if (startHour >= 22 || endHour <= 11) return null;
                  const top = (Math.max(startHour, 11) - 11) * SLOT_HEIGHT;
                  const height = Math.max((Math.min(endHour, 22) - Math.max(startHour, 11)) * SLOT_HEIGHT, 24);
                  const actType = activityTypes.find((t) => t.id === selectedActivityType);
                  const ActivityIcon = actType ? (ICON_MAP[actType.icon] || Clock) : Clock;
                  return (
                    <div
                      className="rounded"
                      style={{
                        position: 'absolute',
                        left: 4,
                        right: 4,
                        top,
                        height: Math.max(height, 24),
                        backgroundColor: '#0d6efd',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '0 0.2rem',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        zIndex: 1,
                      }}
                    >
                      <ActivityIcon size={14} />
                      {formData.title || 'Activity'}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ fontSize: '0.75rem', padding: '0.75rem 1rem', borderTop: '1px solid #dee2e6' }}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                onClick={() => setShowActivityTypesModal(true)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d', display: 'flex', alignItems: 'center' }}
                title="Activity types settings"
              >
                <Gear size={16} />
              </button>
              <div style={{ transform: 'translateY(4px)' }}>
                <Form.Check
                  type="checkbox"
                  id="markAsDone"
                  label="Mark as done"
                  checked={formData.markAsDone}
                  onChange={(e) => handleFormChange('markAsDone', e.target.checked)}
                  style={{ fontSize: '0.75rem', marginBottom: 0 }}
                />
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setShowAddMeetingModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveMeeting}
                disabled={isCreatingMeeting || !formData.title || !formData.startTime || !formData.endTime}
              >
                {isCreatingMeeting ? <Spinner size="sm" animation="border" /> : 'Save'}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Activity Types Settings Modal */}
      <Modal show={showActivityTypesModal} onHide={() => setShowActivityTypesModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Activity types</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '0.5rem' }}>
            Customize the activity types to be used in your company. All activity types marked as active are available when adding a new activity. Deactivated activity types aren&apos;t shown when adding a new activity but remain linked to existing activities.
          </p>
          <Button variant="primary" size="sm" onClick={() => setShowAddActivityTypeModal(true)} className="mb-2" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}>
            <Plus size={12} className="me-1" />
            Activity type
          </Button>
          <div className="d-flex flex-column gap-0" style={{ border: '1px solid #dee2e6', borderRadius: 4, maxHeight: 320, overflowY: 'auto' }}>
            {activityTypes.map((type, index) => {
              const Icon = ICON_MAP[type.icon] || Clock;
              return (
                <div
                  key={type.id}
                  className="d-flex align-items-center gap-1 px-1 py-1"
                  style={{
                    borderBottom: index < activityTypes.length - 1 ? '1px solid #eee' : 'none',
                    fontSize: '0.75rem',
                  }}
                >
                  <div className="d-flex flex-column gap-0" style={{ minWidth: 28 }}>
                    <OverlayTrigger placement="top" overlay={<Tooltip id={`move-up-${type.id}`}>Move up</Tooltip>}>
                      <span>
                        <button
                          type="button"
                          onClick={() => handleMoveActivityType(index, 'up')}
                          disabled={index === 0}
                          style={{
                            background: index === 0 ? '#f8f9fa' : '#e9ecef',
                            border: '1px solid #dee2e6',
                            borderRadius: 4,
                            padding: '0.2rem 0.4rem',
                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                            color: index === 0 ? '#adb5bd' : '#495057',
                            lineHeight: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ArrowUp size={12} />
                        </button>
                      </span>
                    </OverlayTrigger>
                    <OverlayTrigger placement="bottom" overlay={<Tooltip id={`move-down-${type.id}`}>Move down</Tooltip>}>
                      <span>
                        <button
                          type="button"
                          onClick={() => handleMoveActivityType(index, 'down')}
                          disabled={index === activityTypes.length - 1}
                          style={{
                            background: index === activityTypes.length - 1 ? '#f8f9fa' : '#e9ecef',
                            border: '1px solid #dee2e6',
                            borderRadius: 4,
                            padding: '0.2rem 0.4rem',
                            cursor: index === activityTypes.length - 1 ? 'not-allowed' : 'pointer',
                            color: index === activityTypes.length - 1 ? '#adb5bd' : '#495057',
                            lineHeight: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ArrowDown size={12} />
                        </button>
                      </span>
                    </OverlayTrigger>
                  </div>
                  <GripVertical size={12} style={{ color: '#adb5bd' }} />
                  <span style={{ color: '#6c757d' }}><Icon size={14} /></span>
                  <span style={{ flex: 1 }}>{type.label}</span>
                  <div style={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
                    <Form.Switch
                      checked={type.active !== false}
                      onChange={() => handleToggleActivityTypeActive(type.id)}
                    />
                  </div>
                  <OverlayTrigger placement="top" overlay={<Tooltip id={`delete-${type.id}`}>Remove</Tooltip>}>
                    <button
                      type="button"
                      onClick={() => handleDeleteActivityType(type.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '0.2rem',
                        cursor: 'pointer',
                        color: '#6c757d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </OverlayTrigger>
                </div>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActivityTypesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Activity Type Modal */}
      <Modal show={showAddActivityTypeModal} onHide={() => setShowAddActivityTypeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add activity type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Activity type icon</Form.Label>
            <div className="d-flex gap-1 flex-wrap mt-2" style={{ maxHeight: 200, overflowY: 'auto' }}>
              {Object.keys(ICON_MAP).map((iconKey) => {
                const IconComp = ICON_MAP[iconKey];
                const label = iconKey.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                return (
                  <OverlayTrigger key={iconKey} placement="top" overlay={<Tooltip id={`icon-${iconKey}`}>{label}</Tooltip>}>
                    <button
                      type="button"
                      onClick={() => setNewActivityTypeIcon(iconKey)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 6,
                        border: `2px solid ${newActivityTypeIcon === iconKey ? '#0d6efd' : '#dee2e6'}`,
                        backgroundColor: newActivityTypeIcon === iconKey ? '#e7f1ff' : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <IconComp size={18} />
                    </button>
                  </OverlayTrigger>
                );
              })}
            </div>
          </div>
          <div>
            <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Activity type name</Form.Label>
            <Form.Control
              size="sm"
              value={newActivityTypeName}
              onChange={(e) => setNewActivityTypeName(e.target.value)}
              placeholder="e.g. Follow-up"
              style={{ fontSize: '0.85rem' }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddActivityTypeModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddActivityType} disabled={false}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Manage Meeting Purposes Modal */}
      <Modal show={showManagePurposesModal} onHide={() => setShowManagePurposesModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Manage meeting purposes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              Existing purposes
            </div>
            <div className="d-flex flex-column gap-2">
              {meetingPurposeTypes.map((type) => (
                <div key={type.id} className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <span
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: type.color,
                        display: 'inline-block',
                        border: '1px solid rgba(0,0,0,0.12)',
                      }}
                    />
                    <span style={{ fontSize: '0.75rem' }}>{type.name}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      size="sm"
                      type="color"
                      value={purposeColorDrafts[type.id] || type.color}
                      onChange={(e) => handlePurposeColorDraftChange(type.id, e.target.value)}
                      style={{ width: '40px', padding: '0.15rem' }}
                      title={`Change color for ${type.name}`}
                    />
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => handleSavePurposeColor(type.id)}
                      disabled={
                        isSavingPurposeTypes
                        || (purposeColorDrafts[type.id] || '').toLowerCase() === String(type.color || '').toLowerCase()
                      }
                      style={{ fontSize: '0.65rem', padding: '0.12rem 0.45rem' }}
                    >
                      {isSavingPurposeTypes ? <Spinner size="sm" animation="border" /> : 'Save'}
                    </Button>
                    <span style={{ fontSize: '0.65rem', color: '#6c757d', minWidth: '160px', textAlign: 'right' }}>
                      {type.id}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              Add new purpose type
            </div>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                size="sm"
                value={newPurposeName}
                onChange={(e) => setNewPurposeName(e.target.value)}
                placeholder="e.g. QA Sync"
                style={{ fontSize: '0.75rem' }}
              />
              <Form.Control
                size="sm"
                type="color"
                value={newPurposeColor}
                onChange={(e) => setNewPurposeColor(e.target.value)}
                style={{ width: '56px', padding: '0.2rem' }}
                title="Choose purpose color"
              />
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddMeetingPurposeType}
                disabled={isSavingPurposeTypes || !newPurposeName.trim()}
              >
                {isSavingPurposeTypes ? <Spinner size="sm" animation="border" /> : 'Add'}
              </Button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowManagePurposesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Meeting Detail Modal */}
      <Modal show={showMeetingModal} onHide={() => setShowMeetingModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedMeeting?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMeeting && (
            <div>
              <div className="mb-3">
                <strong>Time:</strong> {formatTime(selectedMeeting.startTime)} - {formatTime(selectedMeeting.endTime)}
              </div>
              <div className="mb-3">
                <strong>Purpose:</strong>{' '}
                <span
                  className="rounded-pill"
                  style={{
                    display: 'inline-block',
                    backgroundColor: getMeetingPurposeType(selectedMeeting).color,
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    padding: '0.22rem 0.45rem',
                    lineHeight: 1
                  }}
                >
                  {getMeetingPurposeType(selectedMeeting).name}
                </span>
              </div>
              {selectedMeeting.location && (
                <div className="mb-3">
                  <strong>Location:</strong> {selectedMeeting.location}
                </div>
              )}
              {selectedMeeting.meetingUrl && (
                <div className="mb-3">
                  <strong>Meeting URL:</strong> <a href={selectedMeeting.meetingUrl} target="_blank" rel="noopener noreferrer">{selectedMeeting.meetingUrl}</a>
                </div>
              )}
              {selectedMeeting.description && (
                <div className="mb-3">
                  <strong>Description:</strong>
                  <p>{selectedMeeting.description}</p>
                </div>
              )}
              {selectedMeeting.attendees && selectedMeeting.attendees.length > 0 && (
                <div className="mb-3">
                  <strong>Attendees:</strong>
                  <ul>
                    {selectedMeeting.attendees.map((attendee, idx) => (
                      <li key={idx}>{attendee}</li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedMeeting.notes && (
                <div className="mb-3">
                  <strong>Notes:</strong>
                  <p>{selectedMeeting.notes}</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={handleDeleteSelectedMeeting}
            disabled={isDeletingMeeting || !selectedMeeting?.id}
          >
            {isDeletingMeeting ? <Spinner size="sm" animation="border" /> : 'Delete'}
          </Button>
          <Button variant="secondary" onClick={() => setShowMeetingModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Meetings;
